import { Injectable, inject } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApiGatewayService } from '@wam/authentication';
import { DaysOfWeek } from '@wap/catalog-v5';
import {
  type VisibilityDaySchedule,
  VisibilityOperation,
  VisibilityDay,
  VisibilityBatchResultItem,
  VisibilityBatchItem,
} from '@wap/curriculum-visibility';
import { isNil } from 'lodash-es';
import {
  to24HourFormatEndTime,
  to24HourFormatStartTime,
  toAmPmFormat,
} from '@app/shared/availability-hours-helpers';

/**
 * Visibility rules in the shape used by the cards' UI:
 * 12-hour AM/PM active hours (null when the content is available all day) plus excluded days.
 */
export interface VisibilityRules {
  availableStartHour: string | null;
  availableEndHour: string | null;
  visibilityExcludedDays: DaysOfWeek[];
}

export interface VisibilityRulesTarget {
  documentId: string;
  entity: string;
  grade?: string;
}

interface VisibilityResponse {
  visibility: VisibilityDaySchedule[];
}

const SERVICE_PATH = 'curriculum-visibility/v1';

// The service requires a time window on every enabled day, so "all day" is stored as a full-day window.
export const ALL_DAY_START_TIME = '00:00';
export const ALL_DAY_END_TIME = '23:59';
// Catalog assignments represent midnight-as-end-of-day as 24:00, which the service does not accept.
const CATALOG_END_OF_DAY_TIME = '24:00';

export const ALL_DAY_VISIBILITY_RULES: VisibilityRules = {
  availableStartHour: null,
  availableEndHour: null,
  visibilityExcludedDays: [],
};

@Injectable({
  providedIn: 'root',
})
export class CurriculumVisibilityService {
  private readonly apiGatewayService = inject(ApiGatewayService);

  getVisibilityRules(
    organization: string,
    { documentId, entity, grade }: VisibilityRulesTarget,
  ): Observable<VisibilityRules> {
    return this.apiGatewayService
      .get<VisibilityResponse>(this.visibilityPath(organization, documentId, entity, grade))
      .pipe(map((response) => fromDaySchedules(response?.visibility)));
  }

  // Loads the rules of every target in parallel; a failed target resolves to null so the others still load.
  getVisibilityRulesForTargets(
    organization: string,
    targets: VisibilityRulesTarget[],
  ): Observable<(VisibilityRules | null)[]> {
    if (targets.length === 0) {
      return of([]);
    }

    return forkJoin(
      targets.map((target) =>
        this.getVisibilityRules(organization, target).pipe(catchError(() => of(null))),
      ),
    );
  }

  saveVisibilityRules(
    organization: string,
    { documentId, entity, grade }: VisibilityRulesTarget,
    rules: VisibilityRules,
  ): Observable<VisibilityRules> {
    return this.apiGatewayService
      .put<VisibilityResponse>(
        this.visibilityPath(organization, documentId, entity, grade),
        { visibility: toDaySchedules(rules) },
        'visibility',
      )
      .pipe(map((response) => fromDaySchedules(response?.visibility)));
  }

  saveVisibilityRulesBatch(
    organization: string,
    items: (VisibilityRulesTarget & { rules: VisibilityRules })[],
  ): Observable<VisibilityBatchResultItem[]> {
    if (items.length === 0) {
      return of([]);
    }

    const batchItems: VisibilityBatchItem[] = items.map(({ documentId, entity, grade, rules }) => ({
      type: VisibilityOperation.CREATE_OR_UPDATE,
      documentId,
      entity,
      ...(grade ? { grade } : {}),
      visibility: toDaySchedules(rules),
    }));

    return this.apiGatewayService.put<VisibilityBatchResultItem[]>(
      `${SERVICE_PATH}/orgs/${organization}/visibility`,
      batchItems,
      'visibilityBatchItem',
    );
  }

  private visibilityPath(
    organization: string,
    documentId: string,
    entity: string,
    grade?: string,
  ): string {
    const gradeSegment = grade ? `/grades/${grade}` : '';
    return `${SERVICE_PATH}/orgs/${organization}/documents/${documentId}/entities/${entity}${gradeSegment}/visibility`;
  }
}

export function toDaySchedules(rules: VisibilityRules): VisibilityDaySchedule[] {
  const excludedDays = (rules.visibilityExcludedDays ?? []) as string[];
  const hasActiveHours = !!rules.availableStartHour && !!rules.availableEndHour;
  const startTime = hasActiveHours
    ? to24HourFormatStartTime(rules.availableStartHour)
    : ALL_DAY_START_TIME;
  const rawEndTime = hasActiveHours
    ? to24HourFormatEndTime(rules.availableEndHour)
    : ALL_DAY_END_TIME;
  const endTime = rawEndTime === CATALOG_END_OF_DAY_TIME ? ALL_DAY_END_TIME : rawEndTime;

  return Object.values(VisibilityDay).map((day) =>
    excludedDays.includes(day)
      ? { day, enabled: false }
      : { day, enabled: true, startTime, endTime },
  );
}

export function fromDaySchedules(schedules: VisibilityDaySchedule[] | undefined): VisibilityRules {
  if (isNil(schedules) || schedules.length === 0) {
    return { ...ALL_DAY_VISIBILITY_RULES };
  }

  const visibilityExcludedDays = schedules
    .filter((schedule) => !schedule.enabled)
    .map((schedule) => schedule.day as unknown as DaysOfWeek);

  const activeDay = schedules.find(
    (schedule) => schedule.enabled && !!schedule.startTime && !!schedule.endTime,
  );
  const isAllDay =
    !activeDay ||
    (activeDay.startTime === ALL_DAY_START_TIME && activeDay.endTime === ALL_DAY_END_TIME);

  if (isAllDay) {
    return { ...ALL_DAY_VISIBILITY_RULES, visibilityExcludedDays };
  }

  return {
    availableStartHour: toAmPmFormat(activeDay.startTime),
    availableEndHour: toAmPmFormat(
      activeDay.endTime === ALL_DAY_END_TIME ? CATALOG_END_OF_DAY_TIME : activeDay.endTime,
    ),
    visibilityExcludedDays,
  };
}
